import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface MiningTargetZone {
  id: string;
  start: number;
  end: number;
}

interface MiningMinigameState {
  isActive: boolean;
  progress: number;
  indicator: number;
  hits: number;
  requiredHits: number;
  timeLeftMs: number;
  strikeFeedback: string | null;
}

interface MiningMinigameProps {
  isActive: boolean;
  nodeName: string;
  durationSeconds: number;
  difficultyLevel: number;
  onSuccess: () => void;
  onFailure: () => void;
  onCancel: () => void;
}

const BASE_INDICATOR_SPEED = 0.55; // cycles per second
const SPEED_PER_LEVEL = 0.06;
const BASE_TARGET_WIDTH = 0.15;
const TARGET_WIDTH_STEP = 0.012;
const BASE_REQUIRED_HITS = 3;
const REQUIRED_HITS_STEP = 1;
const FEEDBACK_TIMEOUT_MS = 450;

const clampDifficulty = (level: number): number => Math.max(0, Math.min(6, Math.floor(level)));

const buildTargetZones = (seed: number, requiredHits: number, targetWidth: number): MiningTargetZone[] => {
  const zones: MiningTargetZone[] = [];
  const rng = (index: number): number => {
    const value = Math.sin(seed * (index + 1) * 997) * 10000;
    return value - Math.floor(value);
  };

  for (let i = 0; i < requiredHits; i += 1) {
    const start = Math.max(0.05, Math.min(0.85, rng(i) * 0.85));
    zones.push({
      id: `zone-${i}`,
      start,
      end: Math.min(0.98, start + targetWidth),
    });
  }

  return zones;
};

export const MiningMinigame: React.FC<MiningMinigameProps> = ({
  isActive,
  nodeName,
  durationSeconds,
  difficultyLevel,
  onSuccess,
  onFailure,
  onCancel,
}) => {
  const difficulty = clampDifficulty(difficultyLevel);
  const requiredHits = BASE_REQUIRED_HITS + difficulty * REQUIRED_HITS_STEP;
  const targetWidth = Math.max(0.06, BASE_TARGET_WIDTH - difficulty * TARGET_WIDTH_STEP);
  const indicatorSpeed = BASE_INDICATOR_SPEED + difficulty * SPEED_PER_LEVEL;

  const [state, setState] = useState<MiningMinigameState>({
    isActive: false,
    progress: 0,
    indicator: 0,
    hits: 0,
    requiredHits,
    timeLeftMs: durationSeconds * 1000,
    strikeFeedback: null,
  });
  const lastTimeRef = useRef<number>(Date.now());
  const animationFrameRef = useRef<number | null>(null);
  const feedbackTimeoutRef = useRef<number | null>(null);
  const hitZonesRef = useRef<Set<string>>(new Set());

  const targetZones = useMemo(
    () => buildTargetZones(durationSeconds || 1, requiredHits, targetWidth),
    [durationSeconds, requiredHits, targetWidth],
  );

  const clearFeedback = useCallback((): void => {
    if (feedbackTimeoutRef.current) {
      window.clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = null;
    }
    setState((prev) => ({ ...prev, strikeFeedback: null }));
  }, []);

  const registerFeedback = useCallback(
    (message: string): void => {
      clearFeedback();
      setState((prev) => ({ ...prev, strikeFeedback: message }));
      feedbackTimeoutRef.current = window.setTimeout(() => {
        setState((prev) => ({ ...prev, strikeFeedback: null }));
      }, FEEDBACK_TIMEOUT_MS);
    },
    [clearFeedback],
  );

  const handleSuccess = useCallback((): void => {
    setState((prev) => ({ ...prev, isActive: false }));
    onSuccess();
  }, [onSuccess]);

  const handleFailure = useCallback((): void => {
    setState((prev) => ({ ...prev, isActive: false }));
    onFailure();
  }, [onFailure]);

  const handleStrike = useCallback((): void => {
    if (!isActive) return;

    const hit = targetZones.find((zone) => state.indicator >= zone.start && state.indicator <= zone.end);
    if (!hit || hitZonesRef.current.has(hit.id)) {
      registerFeedback('Miss');
      return;
    }

    hitZonesRef.current.add(hit.id);
    const newHits = hitZonesRef.current.size;
    registerFeedback('Good');

    setState((prev) => ({
      ...prev,
      hits: newHits,
      progress: Math.min(1, newHits / prev.requiredHits),
    }));

    if (newHits >= state.requiredHits) {
      handleSuccess();
    }
  }, [handleSuccess, isActive, registerFeedback, state.indicator, state.requiredHits, targetZones]);

  useEffect(() => {
    if (!isActive) return;

    setState({
      isActive: true,
      progress: 0,
      indicator: 0,
      hits: 0,
      requiredHits,
      timeLeftMs: durationSeconds * 1000,
      strikeFeedback: null,
    });
    hitZonesRef.current = new Set();
    lastTimeRef.current = Date.now();
  }, [durationSeconds, isActive, requiredHits]);

  useEffect(() => {
    if (!isActive) return;

    const update = () => {
      const now = Date.now();
      const deltaMs = now - lastTimeRef.current;
      lastTimeRef.current = now;

      setState((prev) => {
        const timeLeftMs = Math.max(0, prev.timeLeftMs - deltaMs);
        const progress = prev.requiredHits > 0 ? prev.hits / prev.requiredHits : 0;
        const indicator = (prev.indicator + (deltaMs / 1000) * indicatorSpeed) % 1;

        return {
          ...prev,
          indicator,
          timeLeftMs,
          progress,
        };
      });

      animationFrameRef.current = requestAnimationFrame(update);
    };

    animationFrameRef.current = requestAnimationFrame(update);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [indicatorSpeed, isActive]);

  useEffect(() => {
    if (!isActive) return;

    if (state.timeLeftMs <= 0) {
      if (state.hits >= state.requiredHits) {
        handleSuccess();
      } else {
        handleFailure();
      }
    }
  }, [handleFailure, handleSuccess, isActive, state.hits, state.requiredHits, state.timeLeftMs]);

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCancel();
        return;
      }

      if (event.code === 'Space' || event.key === 'Enter') {
        event.preventDefault();
        handleStrike();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleStrike, isActive, onCancel]);

  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) {
        window.clearTimeout(feedbackTimeoutRef.current);
      }
    };
  }, []);

  if (!isActive) return null;

  const timeLeftSeconds = Math.max(0, state.timeLeftMs / 1000);
  const progressPercent = Math.round(state.progress * 100);

  return (
    <div
      className="font-seurat"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(4, 8, 14, 0.7)',
        zIndex: 950,
        pointerEvents: 'auto',
      }}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <div
        style={{
          width: 380,
          padding: 18,
          background: '#0d1117',
          border: '2px solid #2ecc71',
          boxShadow: '0 0 24px rgba(46, 204, 113, 0.3)',
        }}
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ color: '#2ecc71', fontSize: 12, letterSpacing: 1 }}>MINING FOCUS</div>
          <button
            type="button"
            onClick={onCancel}
            className="font-seurat"
            style={{
              background: 'transparent',
              border: '1px solid #30363d',
              color: '#8b949e',
              fontSize: 11,
              padding: '4px 8px',
              cursor: 'pointer',
            }}
          >
            Esc
          </button>
        </div>

        <div style={{ marginTop: 6, color: '#c9d1d9', fontSize: 12 }}>
          {nodeName || 'Mining Node'}
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8b949e' }}>
            <span>{progressPercent}%</span>
            <span>{timeLeftSeconds.toFixed(1)}s</span>
          </div>
          <div
            style={{
              marginTop: 6,
              position: 'relative',
              height: 18,
              background: '#161b22',
              border: '1px solid #30363d',
            }}
          >
            {targetZones.map((zone) => (
              <div
                key={zone.id}
                style={{
                  position: 'absolute',
                  left: `${zone.start * 100}%`,
                  width: `${(zone.end - zone.start) * 100}%`,
                  top: 2,
                  bottom: 2,
                  background: 'rgba(46, 204, 113, 0.3)',
                  border: '1px solid rgba(46, 204, 113, 0.9)',
                }}
              />
            ))}
            <div
              style={{
                position: 'absolute',
                left: `${state.indicator * 100}%`,
                top: -4,
                width: 3,
                height: 26,
                background: '#f1c40f',
                boxShadow: '0 0 8px rgba(241, 196, 15, 0.8)',
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 11, color: '#8b949e' }}>Hits {state.hits}/{state.requiredHits}</div>
          <button
            type="button"
            onClick={handleStrike}
            className="font-seurat"
            style={{
              background: '#2ecc71',
              border: '1px solid #1f9e56',
              color: '#0d1117',
              fontWeight: 700,
              padding: '6px 16px',
              cursor: 'pointer',
              letterSpacing: 0.5,
            }}
          >
            Strike
          </button>
        </div>

        {state.strikeFeedback && (
          <div style={{ marginTop: 10, fontSize: 12, color: state.strikeFeedback === 'Good' ? '#2ecc71' : '#ff7b72' }}>
            {state.strikeFeedback}
          </div>
        )}

        <div style={{ marginTop: 10, fontSize: 11, color: '#8b949e' }}>
          Click or press Space when the marker hits a green zone.
        </div>
      </div>
    </div>
  );
};
